import React, { useMemo } from 'react';
import HeatMap from '@uiw/react-heat-map';
import { DayStats } from '../hooks/useStatistics';
import { useTheme } from '../hooks/useTheme';

interface ActivityHeatmapProps {
  dailyStats: DayStats[];
  formatTime: (seconds: number) => string;
  formatDate: (dateString: string) => string;
}

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ dailyStats, formatTime, formatDate }) => {
  const { theme } = useTheme();

  // Normalize to YYYY/MM/DD which the heatmap library prefers (avoid timezone shifts)
  const normalizeDate = (input: string): string => input.replace(/-/g, '/');

  // Define colors based on theme
  const panelColors = useMemo(() => {
    if (theme === 'light') {
      return {
        0: '#ebedf0',  // Light gray for no activity
        1: '#9be9a8',  // Light green
        2: '#40c463',  // Medium green
        3: '#30a14e',  // Dark green
        4: '#216e39'   // Darkest green
      };
    } else if (theme === 'cyberpunk') {
      return {
        0: '#1a1a30',  // Dark blue-gray
        1: '#00FFD1',  // Cyberpunk cyan
        2: '#00d9b3',  // Medium cyan
        3: '#FF008E',  // Cyberpunk pink
        4: '#4D5BFF'   // Cyberpunk blue
      };
    } else if (theme === 'edge') {
      return {
        0: '#151a2b',  // Edge dark
        1: '#00eaff',  // Edge plasma blue
        2: '#00c5d9',  // Medium plasma
        3: '#ff006e',  // Edge pink
        4: '#ffef00'   // Edge amber
      };
    } else {
      // Default dark theme - GitHub dark mode colors
      return {
        0: '#21262d',  // Dark gray with better contrast
        1: '#0e4429',  // Dark green
        2: '#006d32',  // Medium green  
        3: '#26a641',  // Bright green
        4: '#39d353'   // Brightest green
      };
    }
  }, [theme]);

  // Transform daily stats into heatmap data
  const heatmapValue = useMemo(() => {
    // Only include days that actually have sessions - be more lenient with filtering
    const values = dailyStats
      .filter(dayStats => dayStats.sessions > 0 && dayStats.totalTime >= 0)
      .map(dayStats => {
        // Convert totalTime (seconds) to activity level (1-4)
        // Adjusted for realistic writing session lengths
        let count = 1; // Default to light green for any activity
        if (dayStats.totalTime >= 600) count = 4; // 10+ min - darkest green
        else if (dayStats.totalTime >= 420) count = 3; // 7+ min - dark green  
        else if (dayStats.totalTime >= 240) count = 2; // 4+ min - medium green
        else if (dayStats.totalTime >= 60) count = 1; // 1+ min - light green
        else if (dayStats.totalTime > 0) count = 1; // Any time > 0 gets level 1

        return {
          date: normalizeDate(dayStats.date),
          count: count,
          content: `${dayStats.sessions} session${dayStats.sessions === 1 ? '' : 's'}, total time: ${formatTime(dayStats.totalTime)}`
        };
      });

    return values;
  }, [dailyStats, formatTime]);

  // Calculate start date (same month last year, like GitHub)
  const startDate = useMemo(() => {
    const today = new Date();
    // Start from the same month last year
    const startDate = new Date(today.getFullYear() - 1, today.getMonth(), 1);

    // Align to the Sunday of that week to ensure proper grid alignment
    const dayOfWeek = startDate.getDay();
    if (dayOfWeek !== 0) { // If not Sunday
      startDate.setDate(startDate.getDate() - dayOfWeek);
    }

    return startDate;
  }, []);

  if (heatmapValue.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          📅 Activity Heatmap
        </h2>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No writing activity yet. Start writing to see your activity heatmap!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        📅 Activity Heatmap
      </h2>
      
      <div className="w-full overflow-x-auto">
        <div 
          className="heatmap-container"
          style={{
            '--heatmap-text-color': theme === 'light' ? '#000000' : '#ffffff'
          } as React.CSSProperties}
        >
          <style>{`
            .heatmap-container text {
              fill: var(--heatmap-text-color) !important;
              font-size: 14px !important;
              font-weight: 500 !important;
            }
            .heatmap-container .w-heat-map text {
              fill: var(--heatmap-text-color) !important;
              font-size: 14px !important;
              font-weight: 500 !important;
            }
            .heatmap-container .w-heat-map {
              width: 100% !important;
            }
          `}</style>
          <HeatMap
            value={heatmapValue}
            startDate={startDate}
            endDate={new Date()}
            width="100%"
            weekLabels={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']}
            monthLabels={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']}
            panelColors={panelColors}
            rectSize={13}
            space={3}
            legendCellSize={0}
            monthPlacement="top"
            rectProps={{
              style: { cursor: 'pointer' }
            }}
          rectRender={(props, data) => {
            // Create tooltip content based on whether there's activity data
            const hyphenDate = data.date.replace(/\//g, '-');
            let tooltipContent;
            if (data.count > 0 && data.content) {
              tooltipContent = `${formatDate(hyphenDate)}: ${data.content}`;
            } else {
              tooltipContent = `${formatDate(hyphenDate)}: No activity`;
            }
            
            return (
              <rect 
                {...props}
                style={{ ...props.style, cursor: 'pointer' }}
              >
                <title>{tooltipContent}</title>
              </rect>
            );
          }}
        />
        </div>
        
        {/* Custom Legend */}
        <div className="mt-6 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-6">
            {[
              { level: 0, label: 'No activity' },
              { level: 1, label: '1-4 min' },
              { level: 2, label: '4-7 min' },
              { level: 3, label: '7-10 min' },
              { level: 4, label: '10+ min' }
            ].map(({ level, label }) => (
              <div key={level} className="flex flex-col items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-sm border border-gray-300 dark:border-gray-600"
                  style={{ backgroundColor: panelColors[level as keyof typeof panelColors] }}
                ></div>
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatmap; 